/**
 * @file table.component.tsx
 * @description Collapsed form-control компонент для отображения табличных данных в свернутом состоянии. Представляет собой обертку вокруг компонента таблицы из features с возможностью сворачивания/разворачивания. Поддерживает интерактивные строки с возможностью открытия форм объектов, настраиваемые колонки, контекстные меню и различные типы добавления новых элементов. Используется в формах Task Manager для компактного отображения связанных табличных данных.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-11
 * @dependencies ['@features/table', 'react-hook-form', '@src/types', '@consta/uikit/Collapse', 'react', '@consta/uikit/Text', '@features/object-form', '@utils/form-object-helper']
 * @related-files ['@features/table/table.component.tsx', '@features/object-form/object-form.component.tsx', '@utils/form-object-helper.ts', '@components/form-controls/table/table.component.tsx']
 */

import { Table as DefaultTable } from '@features/table';
import { Controller, Control } from 'react-hook-form';
import { TContextObject, TBaseObjectDto } from '@src/types';
import { Collapse } from '@consta/uikit/Collapse';
import { useRef, useState } from 'react';
import { Text } from '@consta/uikit/Text';
import { ObjectForm } from '@features/object-form';
import { getObjectEntity } from '@utils/form-object-helper';
import { IColumnConfig } from '../../../../types/pages';

/**
 * Props для collapsed form-control компонента Table с интеграцией react-hook-form
 */
interface ITable {
    /** Control объект из react-hook-form для управления формой */
    control: Control;
    /** Имя поля в форме */
    name: string;
    /** Тип объектов для отображения в таблице */
    type: string;
    /** Текст заголовка для свернутой секции */
    label?: string;
    /** Тип объекта для создания новых элементов */
    addItemType?: string;
    /** Разрешить клики по строкам для перехода к объектам */
    isLinkRows?: boolean;
    /** Скрыть заголовок таблицы */
    hideHeader?: boolean;
    /** Конфигурация колонок таблицы */
    columns: IColumnConfig[];
    /** Список элементов контекстного меню */
    contextMenuItemList?: TContextObject[];
    /** Функция-обработчик добавления новой строки */
    addRowHandler?: (item: TBaseObjectDto) => void;
}

export const Table = ({
    control,
    name,
    label,
    type,
    columns,
    hideHeader = false,
    addItemType = 'new',
    ...props
}: ITable) => {
    const [isOpen, setOpen] = useState<boolean>(true);
    const [openedObject, setOpenedObject] = useState<TBaseObjectDto | null>(null);
    const objectEntityRef = useRef<TBaseObjectDto | null>(null);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <>
                    <Collapse label={label} isOpen={isOpen} onClick={() => setOpen(!isOpen)}>
                        {field.value?.length > 0 ? (
                            <DefaultTable
                                items={field.value}
                                columns={columns}
                                className="clickable-rows"
                                onRowClick={(row: TBaseObjectDto) => {
                                    if (!row) {
                                        return;
                                    }

                                    setOpenedObject(row.$item);
                                    objectEntityRef.current = getObjectEntity(row.$item.$type);
                                }}
                                hideHeader={hideHeader}
                                {...props}
                            />
                        ) : (
                            <Text className="w-[42.328vmin]" size="s">
                                Нет данных
                            </Text>
                        )}
                    </Collapse>

                    {addItemType === 'newObject' && openedObject?.$type && objectEntityRef.current && (
                        <ObjectForm
                            type={openedObject?.$type}
                            initialItem={openedObject}
                            onClose={() => {
                                setOpenedObject(null);
                                objectEntityRef.current = null;
                            }}
                            {...objectEntityRef.current}
                        />
                    )}
                </>
            )}
        />
    );
};
